import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * DataSpan component — composable value+unit lockup with CSS class API.
 *
 * Renders an inline-flex container that bottom-aligns a value glyph with a
 * smaller unit glyph (e.g. `42 °F`, `1.2 kW`, `$42`). All visual styling is
 * applied via CSS classes. This wrapper provides:
 * - `data-component="DataSpan"` for component identification
 * - Ref forwarding
 * - `className` merging via `cn()`
 *
 * The `.data-span` base class bakes in the default size (`data-span-default`).
 * Apply `.data-span-value` and `.data-span-unit` to the two slot children;
 * the lockup order is controlled by JSX child order — render the unit first to
 * prepend it, render the value first to append.
 *
 * Currency is NOT a prepended unit: a `$` in the unit slot renders small,
 * normal-weight and tier-3 muted, which reads as detached from the amount.
 * Keep the symbol inline inside the value slot (`$1000`) so it shares the bold
 * value styling. See "Common mistakes" in AGENTS.md and the Currency section of
 * the docs.
 *
 * When NOT to use: DataSpan exists to pair a value with a **unit** (or a
 * value-qualifier like a currency symbol). A bare number with no unit and no
 * qualifier — a count, index, ID, or ratio — is not a DataSpan; it's just
 * text. Render it with `Typography` (e.g. `<Typography as="span"
 * className="text-body-base">`) instead. See the "Without a unit (don't)" story in
 * `data-span.titan.stories.tsx` — stories are not vendored into consumers, so
 * that pointer only resolves in this repo's Storybook.
 *
 * @example
 * ```tsx
 * <DataSpan>
 *   <span className="data-span-value">42</span>
 *   <span className="data-span-unit">°F</span>
 * </DataSpan>
 *
 * <DataSpan className="data-span-sm">
 *   <span className="data-span-value">12</span>
 *   <span className="data-span-unit">ms</span>
 * </DataSpan>
 *
 * // Currency stays inline in the value slot — NOT in the unit slot
 * <DataSpan>
 *   <span className="data-span-value">$42</span>
 * </DataSpan>
 * ```
 */

export type DataSpanProps = React.HTMLAttributes<HTMLSpanElement>;

export const DataSpan = React.forwardRef<HTMLSpanElement, DataSpanProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span ref={ref} data-component="DataSpan" className={cn('data-span', className)} {...props}>
        {children}
      </span>
    );
  }
);

DataSpan.displayName = 'DataSpan';
